// Клиентский BFF-слой auth-домена.
// Источник истины: `contract.md` §1 (BFF-модель), §7.2 (таблица переноса), §7.3 (auth-store
// контракт), `architecture.md` §2.13 (ARCH-D-028 — cookie-модель, без localStorage/Authorization).
//
// Все функции — own-origin `fetch('/api/auth/<method>')` (BFF-нога, см. `pattern_portal_bff.mdc`):
//   • `credentials: 'same-origin'` — браузер прикрепляет httpOnly cookie автоматически
//     (для refresh/logout/me — seentra_refresh/seentra_access; для login/register — guest);
//   • **НЕ** выставляют `Authorization`-заголовок (AGENTS.md, ARCH-D-028);
//   • **НЕ** формируют `/api/method/...` — это серверная прерогатива `portalFetch`;
//   • клиентский JS токен не читает (httpOnly) — cookie ротируются/чистятся сервером.
//
// Успех → распарсенный JSON; ошибка → `PortalApiError` (коды/`extras` проброшены из
// route handler'а, контракт §5.6). Caller (auth-store) решает, что с ошибкой делать.

import type { AuthUserSummary } from '@/lib/auth/auth-store';
import type {
  RegisterPayload,
  LoginPayload,
  ChangePasswordPayload,
  RequestPasswordResetPayload,
  ResetPasswordPayload,
} from '@/lib/api/schemas';
import { PortalApiError } from './errors';
import { refreshRequestInit } from './client-fetch';

/** Развёрнутый body-успех `register` (БЕЗ токенов — они в httpOnly cookie, контракт §5.1). */
export interface RegisterResponseBody {
  success: true;
  detail: string;
  user: AuthUserSummary;
}

/** Развёрнутый body-успех `login`/`refresh` (БЕЗ токенов, контракт §5.2/§5.3). */
export interface AuthUserResponseBody {
  success: true;
  user: AuthUserSummary;
}

/** Развёрнутый body-успех `logout` (контракт §5.4). */
export interface LogoutResponseBody {
  success: true;
  detail: string;
}

interface ErrorResponseBody {
  success: false;
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
    retry_after?: number;
  };
}

/**
 * Парсит BFF-ответ; на `success:false` бросает `PortalApiError` с кодом/status/message/
 * fields/extras из тела. `retry_after` (RATE_LIMITED) прокидывается в `extras` — auth-store/
 * UI может показать countdown (AUTH-CONT-D-002, §7.6).
 */
async function parseAuthResponse<T extends { success: true }>(
  res: Response,
): Promise<T> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // malformed/non-JSON (серверная аномалия, напр. 502 от прокси) — обезличенный INTERNAL_ERROR.
    throw new PortalApiError(
      'INTERNAL_ERROR',
      res.status || 500,
      'Внутренняя ошибка',
    );
  }

  if (body && typeof body === 'object' && (body as { success?: unknown }).success === true) {
    return body as T;
  }

  const errBody = body as ErrorResponseBody | undefined;
  const code = errBody?.error?.code ?? 'INTERNAL_ERROR';
  const message = errBody?.error?.message ?? 'Внутренняя ошибка';
  const fields = errBody?.error?.fields;
  const extras =
    errBody?.error && typeof errBody.error.retry_after === 'number'
      ? { retry_after: errBody.error.retry_after }
      : undefined;
  throw new PortalApiError(code, res.status || 500, message, fields, extras);
}

function commonInit(): RequestInit {
  return {
    method: 'POST',
    credentials: 'same-origin', // httpOnly cookie прикрепляется браузером (ARCH-D-028).
    headers: { 'Content-Type': 'application/json' },
  };
}

/** `POST /api/auth/register` — успех → `{success, detail, user}` (cookie уже стоят). */
export async function registerUser(
  payload: RegisterPayload,
): Promise<RegisterResponseBody> {
  const res = await fetch('/api/auth/register', {
    ...commonInit(),
    body: JSON.stringify({
      ...payload,
      phone: payload.phone ?? '',
      inn: payload.inn ?? '',
    }),
  });
  return parseAuthResponse<RegisterResponseBody>(res);
}

/** `POST /api/auth/login` — успех → `{success, user}` (cookie уже стоят). */
export async function loginUser(payload: LoginPayload): Promise<AuthUserResponseBody> {
  const res = await fetch('/api/auth/login', {
    ...commonInit(),
    body: JSON.stringify(payload),
  });
  return parseAuthResponse<AuthUserResponseBody>(res);
}

/** `POST /api/auth/refresh` — успех → `{success, user}` (cookie ротированы сервером). */
export async function refreshSession(): Promise<AuthUserResponseBody> {
  // Канонический init (method/credentials/headers/body) — в `refreshRequestInit`
  // (FE-05: паритет с `session-refresh-bridge.tsx` и интерсептором `client-fetch`).
  // Пустое тело — refresh-cookie прикрепляется браузером (server route handler пересылает
  // её во Frappe через серверный portalFetch).
  const res = await fetch('/api/auth/refresh', refreshRequestInit());
  return parseAuthResponse<AuthUserResponseBody>(res);
}

/** `POST /api/auth/logout` — успех → `{success, detail}` (идемпотентен, всегда успех на UI). */
export async function logoutUser(): Promise<LogoutResponseBody> {
  const res = await fetch('/api/auth/logout', {
    ...commonInit(),
    body: '{}',
  });
  return parseAuthResponse<LogoutResponseBody>(res);
}

/** `GET /api/auth/me` — успех → `{success, user}`. Используется редко (основной потребитель
 *  `me` — серверный RSC guard `(protected)/layout.tsx` через `portalFetch`). */
export async function getCurrentUser(): Promise<AuthUserResponseBody> {
  const res = await fetch('/api/auth/me', {
    credentials: 'same-origin',
    headers: { 'Cache-Control': 'no-store' },
  });
  return parseAuthResponse<AuthUserResponseBody>(res);
}

// =============================================================================
// add-credentials-management — клиентские функции 3 методов (контракт §D).
// НЕ через `clientFetch` (refresh-on-401-loop) — parity loginUser/registerUser
// (auth.cs.md §12.2): own-origin fetch + parseAuthResponse.
// =============================================================================

/** `POST /api/auth/change-password` — успех → `{detail}`; ошибка → throw PortalApiError.
 *  Сессия сохранена (cookie не трогает). `INVALID_CURRENT_PASSWORD`/`VALIDATION_ERROR`/
 *  `RATE_LIMITED` пробрасываются из route handler'а. */
export async function changeMyPassword(
  payload: ChangePasswordPayload,
): Promise<{ detail: string }> {
  const res = await fetch('/api/auth/change-password', {
    ...commonInit(),
    body: JSON.stringify(payload),
  });
  const data = await parseAuthResponse<{ success: true; detail: string }>(res);
  return { detail: data.detail };
}

/** `POST /api/auth/request-password-reset` — успех → `{detail}` (унифицированный,
 *  не раскрывает существование аккаунта); ошибка → throw. */
export async function requestPasswordReset(
  payload: RequestPasswordResetPayload,
): Promise<{ detail: string }> {
  const res = await fetch('/api/auth/request-password-reset', {
    ...commonInit(),
    body: JSON.stringify(payload),
  });
  const data = await parseAuthResponse<{ success: true; detail: string }>(res);
  return { detail: data.detail };
}

/** `POST /api/auth/reset-password` — успех → `{user, detail}` (cookie уже стоят —
 *  autologin, route handler setAuthCookies); ошибка → throw PortalApiError
 *  (`RESET_KEY_INVALID`/`VALIDATION_ERROR`/`RATE_LIMITED`). Возвращает `user` для
 *  `useAuth().setUser(data.user)` (parity registerUser). */
export async function resetMyPassword(
  payload: ResetPasswordPayload,
): Promise<{ user: AuthUserSummary; detail: string }> {
  const res = await fetch('/api/auth/reset-password', {
    ...commonInit(),
    body: JSON.stringify(payload),
  });
  const data = await parseAuthResponse<{ success: true; detail: string; user: AuthUserSummary }>(
    res,
  );
  return { user: data.user, detail: data.detail };
}
